import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject, LOCALE_ID, signal } from '@angular/core';
import { FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { forkJoin, lastValueFrom, Observable, Subscription } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { ConnectionCatalog } from '../models/connection-catalog.model';
import { GradeCatalog } from '../models/grade-catalog.model';
import { PipesCatalog } from '../models/pipes-catalog.model';
import { CatalogScope, CatalogFilterMode } from '../models/catalog-scope.enum';
import { ConnectionsService } from '../shared/services/connections.service';
import { GradesService } from '../shared/services/grades-catalog.service';
import { PipesService } from '../shared/services/pipes-catalog.service';
import { connectionOdSmallerThanPipeOd } from '../validation/connectionOdSmallerThanPipeOd';
import { innerSmallerThanOuterDiameter } from '../validation/innerSmallerThanOuterDiameter';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { saveAs } from 'file-saver-es';
import ExcelJS from "exceljs";
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { Utilities } from 'src/app/core/services/utilities';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import {
  createConnectionCqeColumns,
  createConnectionCqeRow,
  ensureTrailingInputRow,
  extractConnectionCqeRows,
  populateConnectionCqeFormArray
} from './connection-cqe-grid.helper';
import { getDuplicateCatalogColors } from '../shared/helpers/catalog-duplicate-highlight.helper';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';
import { UnitPrecisionOverrides } from 'src/app/shared/helpers/unit-precision.helper';

@Component({
  selector: 'app-connections-catalog',
  templateUrl: './connections-catalog.component.html',
  styles: `
    .catalog-row-disabled > td:not(:first-child):not(.catalog-row-cqe) {
      opacity: 0.6;
      pointer-events: none;
    }

  `,
  providers: [DialogService, ConfirmationService],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConnectionsCatalogComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _connections: ConnectionCatalog[]
  private _pipes: PipesCatalog[];
  private _updateTle: boolean;
  private _filteredConnections: ConnectionCatalog[] = [];
  private _selectedPipeOdFilter = null;
  private readonly _locale = inject(LOCALE_ID);

  public readonly pipeDimensionPrecisionOverrides = signal<UnitPrecisionOverrides>({});
  public readonly weightPrecisionOverrides = signal<UnitPrecisionOverrides>({});
  public linearDensityUnit: string;
  public columnDefs: any[];
  public grades: { label: string, value: GradeCatalog }[] = [];
  public pipeOds: { label: string, value: number }[] = [];
  public isLoading = signal<boolean>(true);
  public isLoadingCqe = signal<boolean>(true);
  public diameterUnit: string;
  public weightUnit: string;
  public pressureUnit: string;
  public forceUnit: string;
  public connectionsForm: UntypedFormGroup;
  public tableHeight: string = '600px';
  public userRoles: UserRoles;
  public filterPipeOds: { label: string, value: number }[] = [];
  public tleDialogVisible = false;
  public updatedTleProfile: any[] = [];
  public tleProfileRows = new FormArray<FormGroup>([]);
  public tleColumns: readonly UnitsGridColumn[] = [];
  public readonly createTleRow = createConnectionCqeRow;
  public tleTablePlotSelected = 'table';
  public xAxisTitle: string;
  public yAxisTitle: string;
  public connectionFg: UntypedFormGroup;
  public rows = 15;
  public paginatorFirst = signal<number>(0);
  public duplicateRowColors = signal<ReadonlyMap<string, string>>(new Map());

  public plot = {
    data: [],
    layout: {},
    config: {}
  };

  public tleTablePlot = [
    { label: 'Table', value: 'table' },
    { label: 'Plot', value: 'plot' }
  ];

  // Scope filtering
  public CatalogScope = CatalogScope; // Expose enum to template
  public selectedScopeFilter: CatalogFilterMode = CatalogFilterMode.AllItems;

  get connectionsCatalog(): UntypedFormArray {
    return this.connectionsForm.get("connections") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  // State
  @Input()
  public componentId: string;

  @ViewChild("catalogTable") catalogTable;

  constructor(
    private _messenger: MediatorService,
    private _pipesCatalogService: PipesService,
    private _connectionsCatalogService: ConnectionsService,
    private _gradesCatalogService: GradesService,
    private _formBuilder: UntypedFormBuilder,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private _store: StoreService,
    private _toaster: AppNotificationService
  ) {
    this.connectionsForm = this._formBuilder.group({
      connections: this._formBuilder.array([]),
    });

    this._subscriptions.add(
      this.connectionsCatalog.valueChanges.pipe(debounceTime(0)).subscribe(() => this.refreshDuplicateRowColors())
    );
  }

  async ngOnInit(): Promise<void> {
    this.userRoles = await this._store.get<UserRoles>(StorageKeys.ROLES);
    const uu = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.diameterUnit = uu.shortLengths;
    this.pipeDimensionPrecisionOverrides.set({ [uu.shortLengths]: 3 });
    this.linearDensityUnit = uu.linearDensity;
    this.weightPrecisionOverrides.set({ [uu.linearDensity]: 3 });
    this.weightUnit = unitsLib[uu.linearDensity].symbol;
    this.pressureUnit = uu.pressure;
    this.forceUnit = uu.force;
    this.yAxisTitle = `Pressure (${this.pressureUnit})`;
    this.xAxisTitle = `Axial Load (${this.forceUnit})`;

    this.tleColumns = createConnectionCqeColumns(this.forceUnit, this.pressureUnit);

    this.columnDefs = [
      {
        headerName: 'Pipe',
        children: [
          { field: 'pipeOd', headerName: `OD (${uu.shortLengths})` },
          { field: 'tubularPipeId', headerName: `Pipe ID (${uu.shortLengths}, ${this.weightUnit})` }
        ],
      },
      {
        headerName: 'Connection',
        children: [
          { field: 'outerDiameter', headerName: `Outer Diameter (${uu.shortLengths})` },
          { field: 'innerDiameter', headerName: `Inner Diameter (${uu.shortLengths})` },
          { field: 'gradeId', headerName: 'Grade' },
          { field: 'burstRating', headerName: `Burst Rating (${uu.pressure})` },
          { field: 'collapseRating', headerName: `Collapse Rating * (${uu.pressure})` },
          { field: 'tensionRating', headerName: `Tension Rating (${this.forceUnit})` },
          { field: 'compressionRating', headerName: `Compression Rating (${this.forceUnit})` },
        ],
      },
      {
        headerName: '',
        children: [
          { field: 'name', headerName: 'Name' }
        ]
      }
    ];

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, (data) => this.signalRfunc(data));

    this.getConnectionsData(false);

    addEventListener("keydown", (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  public onScopeFilterChange(): void {
    this.connectionsCatalog.clear();
    this.getConnectionsData(false);
  }

  private signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_GRADES_CATALOG ||
      data.action == PeriforOnChangeMessages.REFRESH_PIPES_CATALOG) {
      this._connections = [];
      this.getConnectionsData(true);
    }
  }

  private getConnectionsData(isCalledFromSignalR: boolean) {
    this.paginatorFirst.set(0);
    const sources: Observable<any>[] = [
      this._pipesCatalogService.getPipes(),
      this._pipesCatalogService.getPipeOds(),
      this._gradesCatalogService.getGrades(),
      this._connectionsCatalogService.getConnections(this.selectedScopeFilter)
    ];

    forkJoin(sources).pipe(
      map(([pipes, pipeOds, grades, connections]) => {
        this._connections = connections.sort((a, b) => {
          if (!a.name && !b.name) return 0;
          if (!a.name) return 1;
          if (!b.name) return -1;
          return a.name.localeCompare(b.name);
        });
        this._pipes = pipes.filter(p => p.discriminator == 'tubularPipe');
        this.pipeOds = pipeOds.map(x => ({ label: x.outsideDiameter.toString(), value: x.outsideDiameter }));
        this.filterPipeOds = [
          ...new Map(
            this.pipeOds
              .filter(x => this._connections.some(c => c.tubularPipe?.outsideDiameter === x.value))
              .map(x => [x.value, x])
          ).values()
        ];
        this.filterPipeOds.unshift({ label: 'View All', value: 0 });
        this.grades = grades.filter(x => x.discriminator == 'TubularPipe').map(g => ({ label: g.name, value: g }));

        if (!isCalledFromSignalR) {
          if (this._selectedPipeOdFilter) {
            connections = connections.filter(c => c.tubularPipe.outsideDiameter == this._selectedPipeOdFilter);
          }
          this.populateFormData(connections);
          if (this.catalogTable) {
            this.catalogTable.first = 0;
          }
        }

        setTimeout(() => {
          this.isLoading.set(false);
        }, 100);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public onPageChange(event: { first?: number }): void {
    this.paginatorFirst.set(event.first ?? 0);
  }

  public onAddConnection(idx?: number): void {
    this.connectionsCatalog.insert(idx || 0, this.newConnection());
    this.paginatorFirst.set(0);
    if (this.catalogTable) {
      this.catalogTable.clearState?.();
      this.catalogTable.first = 0;
    }
    this.catalogTable?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public onDeleteConnection(idx: number): void {
    const connectionId = this.connectionsCatalog.controls[idx].get("id").value;
    const connectionName = this.connectionsCatalog.controls[idx].get("name").value;

    if (connectionId.length < 36) {
      this.connectionsCatalog.removeAt(idx);
    } else {
      this._confirmationService.confirm({
        message: `Are you sure that you want to delete the selected "${connectionName}" connection?`,
        accept: async () => {
          if (connectionId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._connectionsCatalogService.deleteConnection(connectionId));
            this._messenger.publish(new DataSavedMessage('Connections Catalog'));

            const pipeOd = this.connectionsCatalog.controls[idx].get("pipeOd").value;
            if (pipeOd && this.filterPipeOds.some(x => x.value == pipeOd)) {
              const connectionsUsingThisPipeOd = this.connectionsCatalog.controls.filter(c => c.get("pipeOd").value == pipeOd);
              if (connectionsUsingThisPipeOd.length == 1) {
                this.filterPipeOds = this.filterPipeOds.filter(x => x.value != pipeOd);
              }
            }
          }
          this.connectionsCatalog.removeAt(idx);
        }
      });
    }
  }

  public deleteConnectionCheck(connFg: FormGroup): boolean {
    const isAdmin = !!this.userRoles?.catalogAdmin;
    const scope = (connFg.get('scope')?.value ?? CatalogScope.User) as CatalogScope;
    const isLocked = !!connFg.get('isLocked')?.value;

    return isLocked || (!isAdmin && scope !== CatalogScope.User);
  }

  public showJson(data: any) {
    return JSON.stringify(data, null, '\t');
  }

  public setHoverTitle(event: Event, title: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      target.setAttribute('title', String(title ?? ''));
    }
  }

  public getScopeLabel(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'Global' : 'User';
  }

  public getScopeBadgeClass(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  }

  public async toggleScope(formGroup: UntypedFormGroup): Promise<void> {
    this.isLoading.set(true);
    const currentScope = formGroup.get('scope').value;
    const newScope = currentScope === CatalogScope.User ? CatalogScope.Global : CatalogScope.User;

    const connectionData = formGroup.value;
    connectionData.scope = newScope;

    await lastValueFrom(this._connectionsCatalogService.updateConnection(new ConnectionCatalog(connectionData)));

    // Only update the form if save succeeds
    formGroup.get('scope').patchValue(newScope, { emitEvent: false });

    // Update the local cache
    const index = this._connections.findIndex(x => x.id === connectionData.id);
    if (index > -1) {
      this._connections[index].scope = newScope;
    }

    this._messenger.publish(new DataSavedMessage('Connections Catalog'));
    this.isLoading.set(false);
  }

  public getPipeOdPlaceholder(pipe: PipesCatalog | null | undefined): string {
    return typeof pipe?.outsideDiameter === 'number'
      ? formatUnitNumber(pipe.outsideDiameter, this.diameterUnit, this._locale, undefined, this.pipeDimensionPrecisionOverrides())
      : 'Select';
  }

  public getPlaceholderText(data: any, field: string, isPipeOdFilter?: boolean): string {
    let text = data && data.id ? `${data[field]} (local only)` : "Select";

    if (field == 'name') {
      const gradeExists = this.grades.find(x => x.value?.name == data?.name &&
        x.value?.minimumApiYieldStrength == data?.minimumApiYieldStrength && x.value?.youngsModulus == data?.youngsModulus && x.value?.poissonsRatio == data?.poissonsRatio &&
        x.value?.thermalExpansionCoefficient == data?.thermalExpansionCoefficient && x.value?.ultimateTensileStrength == data?.ultimateTensileStrength &&
        x.value?.density == data?.density && x.value?.specificHeatCapacity == data?.specificHeatCapacity && x.value?.temperatureDeration?.name == data?.temperatureDeration?.name);
      if (gradeExists) {
        text = this.grades.find(x => x.value?.name == data?.name).label;
      } else {
        text = data && data.id ? `${data[field]} (local only)` : "Select";
      }
    }

    if (field == 'outsideDiameter' && !isPipeOdFilter) {
      text = (data && data.id.length > 0) ? data && data.id && this.pipeOds.find(x => x.value == data.outsideDiameter) ? `${data[field]}` : `${data[field]} (local only)` : 'Select';
    }

    if (field == 'outsideDiameter' && isPipeOdFilter) {
      text = (data && data.id.length > 0) ? data && data.id && this.pipeOds.find(x => x.value == data.outsideDiameter) ? `${data[field]}` : `${data[field]}` : 'View All';
    }

    return text;
  }

  private findExactMatch(v: any, checkId: boolean): ConnectionCatalog | undefined {
    return this._connections.find(c =>
      c.name != null &&
      c.outerDiameter != null &&
      c.innerDiameter != null &&
      c.burstRating != null &&
      c.collapseRating != null &&
      c.tensionRating != null &&
      c.compressionRating != null &&
      c.grade != null &&
      c.scope != null &&
      (checkId ? c.id != v.id : false) &&
      c.name == v.name &&
      this.approximatelyEqual(c.outerDiameter, v.outerDiameter) &&
      this.approximatelyEqual(c.innerDiameter, v.innerDiameter) &&
      this.approximatelyEqual(c.burstRating, v.burstRating) &&
      this.approximatelyEqual(c.collapseRating, v.collapseRating) &&
      this.approximatelyEqual(c.tensionRating, v.tensionRating) &&
      this.approximatelyEqual(c.compressionRating, v.compressionRating) &&
      c.grade.name == v.grade.name && c.grade.temperatureDeration?.name == v.grade.temperatureDeration?.name &&
      c.tubularPipe?.outsideDiameter == v.tubularPipe?.outsideDiameter &&
      c.tubularPipe?.weightPerFoot == v.tubularPipe?.weightPerFoot &&
      c.scope == v.scope
    );
  }

  private async handleSaveConnectionRow(v: any, connectionRecord: UntypedFormGroup): Promise<void> {
    if (this.findExactMatch(v, false) && !this._updateTle && connectionRecord.controls.grade.pristine && connectionRecord.controls.pipeOd.pristine) {
      return;
    }
    this._updateTle = false;

    if (connectionRecord.valid && !this.isLoading()) {
      if (this.findExactMatch(v, true)) {
        this._toaster.showError(`Connection ${v.name} with same properties already exists.`);
        return;
      }
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        this.isLoading.set(true);
        const newEntityId = await lastValueFrom(this._connectionsCatalogService.addConnection(new ConnectionCatalog(v)));
        connectionRecord.get("id").patchValue(newEntityId, { emitEvent: false });
        this._connections.unshift(connectionRecord.value);
        this.isLoading.set(false);
      } else {
        await lastValueFrom(this._connectionsCatalogService.updateConnection(new ConnectionCatalog(v)));
        const index = this._connections.findIndex(x => x.id === v.id);
        if (index > -1) {
          this._connections[index] = v;
        }
      }
      this._messenger.publish(new DataSavedMessage('Connections Catalog'));
      if (v.pipeOd && !this.filterPipeOds.some(x => x.value == v.pipeOd)) {
        this.filterPipeOds.push({ label: v.pipeOd.toString(), value: v.pipeOd });
        this.filterPipeOds.sort((a, b) => b.value - a.value);
      }
    }
  }

  approximatelyEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== 'number' || typeof b !== 'number') return false;
    return Math.abs(a - b) < Number.EPSILON * Math.max(Math.abs(a), Math.abs(b));
  }

  private newConnection(): UntypedFormGroup {
    const connectionFg = new UntypedFormGroup({
      // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      id: new UntypedFormControl(Utilities.generateFakeGuid()),
      name: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      pipeOd: new UntypedFormControl(""),
      tubularPipe: new UntypedFormControl("", [Validators.required]),
      grade: new UntypedFormControl(this.grades && this.grades.length > 0 ? this.grades[0].value : {}, [Validators.required]),
      outerDiameter: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required, connectionOdSmallerThanPipeOd] }),
      innerDiameter: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required, innerSmallerThanOuterDiameter] }),
      burstRating: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      collapseRating: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      tensionRating: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      compressionRating: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      tubularPipeLookup: new UntypedFormControl(),
      isLocked: new UntypedFormControl(false),
      metadata: new UntypedFormControl(null),
      scope: new UntypedFormControl(CatalogScope.User),
      tleProfile: new UntypedFormControl(null),
    });

    this._subscriptions.add(connectionFg.valueChanges.pipe(debounceTime(100)).subscribe((v) => this.handleSaveConnectionRow(v, connectionFg)));

    this._subscriptions.add(connectionFg.controls.pipeOd.valueChanges.subscribe((v) => {
      this.onPipeOdChange(v, connectionFg)
    }));

    const pipeOd = connectionFg.get("pipeOd");
    if (this.pipeOds && this.pipeOds.length > 0) {
      pipeOd.patchValue(this.pipeOds[0].value, { emitEvent: false });
    }

    this._subscriptions.add(pipeOd.valueChanges.subscribe((e) => {
      const pipeLookup = this.getPipesFilteredByOd(e);
      connectionFg.get("tubularPipeLookup").patchValue(pipeLookup, { emitEvent: false });
    }));

    if (this.pipeOds && this.pipeOds.length > 0) {
      const pipeLookup = this.getPipesFilteredByOd(this.pipeOds[0].value);
      if (pipeLookup && pipeLookup.length > 0) {
        connectionFg.get("tubularPipe").patchValue(pipeLookup[0].value, { emitEvent: false });
        connectionFg.get("tubularPipeLookup").patchValue(pipeLookup, { emitEvent: false });
      }
    }

    connectionFg.markAllAsTouched();

    return connectionFg;
  }

  public async onPipeOdChange(pipeOd: number, tubularFg: UntypedFormGroup): Promise<void> {
    if (pipeOd) {
      const pipeCtrl = tubularFg.get('tubularPipe') as UntypedFormControl;
      const pipe = this._pipes.find(p => p.outsideDiameter == pipeOd);
      pipeCtrl.patchValue(pipe, { emitEvent: false });

      const odCtrl = tubularFg.get('outerDiameter') as UntypedFormControl;
      odCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private getPipesFilteredByOd(od: number) {
    const filteredPipes = this._pipes.filter(p => p.outsideDiameter == od);
    const weightUnit = this.weightUnit == 'lb/ft' ? "#" : this.weightUnit;
    const diameterUnit = this.diameterUnit == "in" ? '"' : this.diameterUnit;
    const pipeLookup = filteredPipes.map(p => {
      return {
        label: `${p.outsideDiameter}${diameterUnit}, ${p.weightPerFoot}${weightUnit}`,
        value: p
      };
    });
    return pipeLookup;
  }

  public getSortedPipeOptions(rowData: any, parameter: any): PipesCatalog[] {
    const data = rowData.get('tubularPipeLookup').value;
    return data ? [...data].map(o => o.value as PipesCatalog).sort((a, b) => (a[parameter] < b[parameter] ? -1 : 1)) : [];
  }

  public readonly formatPipeLabel = (pipe: PipesCatalog): string => {
    const diameterSuffix = this.diameterUnit == 'in' ? '"' : this.diameterUnit;
    const weightSuffix = this.weightUnit == 'lb/ft' ? '#' : this.weightUnit;
    const od = formatUnitNumber(pipe.outsideDiameter, this.diameterUnit, this._locale, undefined, this.pipeDimensionPrecisionOverrides());
    const weight = formatUnitNumber(pipe.weightPerFoot, this.linearDensityUnit, this._locale, undefined, this.weightPrecisionOverrides());
    return `${od}${diameterSuffix}, ${weight}${weightSuffix}`;
  };

  public getPipePlaceholder(pipe: PipesCatalog | null | undefined): string {
    if (typeof pipe?.outsideDiameter !== 'number' || typeof pipe?.weightPerFoot !== 'number') {
      return 'Select';
    }
    const label = this.formatPipeLabel(pipe);
    return this.pipeOds.some(x => x.value == pipe.outsideDiameter) ? label : `${label} (local only)`;
  }

  private populateFormData(connections?: ConnectionCatalog[]) {
    this.connectionsCatalog.clear();
    connections.forEach((data, idx) => {
      const connectionFg = this.newConnection();
      this.connectionsCatalog.push(connectionFg);
      const pipeOd = this._pipes.find(p => p.id == data.tubularPipe?.id)?.outsideDiameter || "";

      const pipeLookup = this.getPipesFilteredByOd(pipeOd as any);

      // Find the matching pipe from pipeLookup to ensure object reference matches
      const matchingPipe = pipeLookup.find(p => p.value.id === data.tubularPipe?.id)?.value || data.tubularPipe;

      const expandedData = {
        ...data,
        tubularPipe: matchingPipe, // Use the pipe object from pipeLookup options
        tubularPipeLookup: pipeLookup,
        pipeOd: pipeOd
      };
      this.connectionsCatalog.controls[idx].patchValue(expandedData, { emitEvent: false });
      this.syncGradeRowState(this.connectionsCatalog.controls[idx] as FormGroup);
    });
    this.refreshDuplicateRowColors();
    setTimeout(() => {
      this.isLoading.set(false);
    }, 50);
  }

  private refreshDuplicateRowColors(): void {
    if (!this.userRoles?.catalogAdmin) {
      this.duplicateRowColors.set(new Map());
      return;
    }

    this.duplicateRowColors.set(getDuplicateCatalogColors(this.connectionsCatalog.getRawValue(), connection => ({
      name: connection.name ?? null,
      pipeOutsideDiameter: connection.tubularPipe?.outsideDiameter ?? null,
      pipeWeightPerFoot: connection.tubularPipe?.weightPerFoot ?? null,
      gradeName: connection.grade?.name ?? null,
      temperatureDerationName: connection.grade?.temperatureDeration?.name ?? null,
      outerDiameter: connection.outerDiameter ?? null,
      innerDiameter: connection.innerDiameter ?? null,
      burstRating: connection.burstRating ?? null,
      collapseRating: connection.collapseRating ?? null,
      tensionRating: connection.tensionRating ?? null,
      compressionRating: connection.compressionRating ?? null,
      scope: connection.scope ?? null
    })));
  }

  private syncGradeRowState(connectionFg: FormGroup): void {
    const shouldDisable = this.updateFormDisabledState(connectionFg);

    if (shouldDisable && connectionFg.enabled) {
      connectionFg.disable({ emitEvent: false });
      return;
    }

    if (!shouldDisable && connectionFg.disabled) {
      connectionFg.enable({ emitEvent: false });
    }
  }

  public updateFormDisabledState(connectionFg: FormGroup): boolean {
    return this.deleteConnectionCheck(connectionFg) || this.userRoles?.readOnly;
  }

  public async exportExcel(): Promise<void> {
    const flatColumns: { field: string; header: string }[] = this.columnDefs.flatMap(cd =>
      (cd.children ?? []).map(child => ({ field: child.field, header: child.headerName }))
    );

    const mappedData = this.connectionsCatalog.controls.map(td => {
      const objWithHeaderKeys = {};
      flatColumns.forEach(cd => {
        const objHeaderKey = cd.header;
        let name = '';
        if (cd.field == 'tubularPipeId') {
          name = `${td.value.tubularPipe.outsideDiameter}` + ', ' + `${td.value.tubularPipe.weightPerFoot}`;
        } else if (cd.field == 'gradeId') {
          name = td.value.grade.name;
        } else {
          name = td.value[cd.field];
        }
        objWithHeaderKeys[objHeaderKey.replace(/["']/g, '"')] = name;
      });
      return objWithHeaderKeys;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Connections Catalog");

    worksheet.columns = Object.keys(mappedData[0]).map((key) => ({
      header: key,
      key: key,
      width: 45
    }));

    worksheet.addRows(mappedData);

    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, "Connections Catalog");
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + '_export_' + EXCEL_EXTENSION);
  }

  public filterByOd(e) {
    this.connectionsCatalog.clear();
    this._selectedPipeOdFilter = e.value == 0 ? null : e.value;
    this._filteredConnections = e.value == 0 ? this._connections : this._connections.filter(c => c.tubularPipe.outsideDiameter == e.value);
    this.populateFormData(this._filteredConnections);
    this.paginatorFirst.set(0);
    if (this.catalogTable) {
      this.catalogTable.first = 0;
    }
    this.catalogTable?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  public openTleDialog(connFg: UntypedFormGroup): void {
    this.updatedTleProfile = [];
    this.isLoadingCqe.set(true);
    setTimeout(() => {
    const connections = this._filteredConnections.length > 0 ? this._filteredConnections : this._connections;
      this.updatedTleProfile = connections.find(c => c.id == connFg.get('id').value)?.tleProfile || [];
      if (!this.updatedTleProfile || this.updatedTleProfile.length == 0) {
        this.updatedTleProfile = [{ axialLoad: 0, pressure: 0 }];
      }
      populateConnectionCqeFormArray(this.tleProfileRows, this.updatedTleProfile);
      this.connectionFg = connFg;
      this.tleDialogVisible = true;
      this.isLoadingCqe.set(false);
    }, 50);
  }

  public tablePlotToggle() {
    this.syncTleProfile();
    const data: any = this.updatedTleProfile;
    if (!data) return;

    const xArr = data.map(d => d.pressure);
    const yArr = data.map(d => d.axialLoad);

    if (xArr[0] !== xArr[xArr.length - 1] || yArr[0] !== yArr[yArr.length - 1]) {
      xArr.push(xArr[0]);
      yArr.push(yArr[0]);
    }

    const traceArray: any[] = [
      {
        y: xArr,
        x: yArr,
        mode: 'markers+lines',
        marker: { size: 2 },
        name: 'CQE',
        showlegend: false
      }
    ];

    this.plot.data = traceArray;
  }

  public saveTleData() {
    this.syncTleProfile();
    const selectedConnection = this._connections.find(c => c.id == this.connectionFg.get('id').value);
    selectedConnection.tleProfile = this.updatedTleProfile;
    this.handleSaveConnectionRow(selectedConnection, this.connectionFg);
    this.tleTablePlotSelected = 'table';
    this.tleDialogVisible = false;

    this._confirmationService.confirm({
      header: 'Connection Qualification Envelope has been changed',
      icon: PrimeIcons.INFO_CIRCLE,
      message: `Please make sure to re-select the Connection if it has already been used in the Well Configuration.`,
      acceptLabel: 'OK',
      rejectVisible: false
    });
  }

  public onCloseTleDialog() {
    this.tleTablePlotSelected = 'table';
    this.tleDialogVisible = false;
  }

  public onTleCellEdited(): void {
    ensureTrailingInputRow(this.tleProfileRows);
  }

  public onTleRowDeleted(index: number): void {
    if (index >= 0 && index < this.tleProfileRows.length) {
      this.tleProfileRows.removeAt(index);
    }
    this.onTleGridCommit();
  }

  public onTleRowsReset(): void {
    populateConnectionCqeFormArray(this.tleProfileRows, []);
    this.syncTleProfile();
  }

  public onTleGridCommit(): void {
    ensureTrailingInputRow(this.tleProfileRows);
    this.syncTleProfile();
  }

  private syncTleProfile(): void {
    this.updatedTleProfile.splice(0, this.updatedTleProfile.length, ...extractConnectionCqeRows(this.tleProfileRows));
    this._updateTle = true;
  }

  public checkTleDataExists(rIdx: number): boolean {
    const connections = this._filteredConnections.length > 0 ? this._filteredConnections : this._connections;
    return connections && connections[rIdx]?.tleProfile && connections[rIdx]?.tleProfile.length > 0;
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
